import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import Language from "../../../services/Language.js";
import "./style.css";

const l = new Language();

const CHARS = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
const CODE_LENGTH = 5;
const CANVAS_W = 180;
const CANVAS_H = 48;

const rnd = (max) => Math.floor(Math.random() * max);

const generateCode = () => {
  let result = "";
  for (let i = 0; i < CODE_LENGTH; i++) {
    result += CHARS.charAt(Math.floor(Math.random() * CHARS.length));
  }
  return result;
};

const drawCaptcha = (canvas, text) => {
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  const w = CANVAS_W;
  const h = CANVAS_H;

  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = "#f4f6f8";
  ctx.fillRect(0, 0, w, h);

  // noise lines
  for (let i = 0; i < 5; i++) {
    ctx.strokeStyle = `rgba(${rnd(180)},${rnd(180)},${rnd(180)},0.45)`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(rnd(w), rnd(h));
    ctx.lineTo(rnd(w), rnd(h));
    ctx.stroke();
  }

  // characters
  const colors = ["#1565C0", "#B71C1C", "#1B5E20", "#E65100", "#4A148C"];
  text.split("").forEach((char, i) => {
    ctx.save();
    ctx.font = `bold ${22 + rnd(5)}px monospace`;
    ctx.fillStyle = colors[i % colors.length];
    ctx.translate(14 + i * 33, 32 + rnd(8) - 4);
    ctx.rotate((Math.random() - 0.5) * 0.4);
    ctx.fillText(char, 0, 0);
    ctx.restore();
  });

  // noise dots
  for (let i = 0; i < 40; i++) {
    ctx.fillStyle = `rgba(${rnd(180)},${rnd(180)},${rnd(180)},0.45)`;
    ctx.beginPath();
    ctx.arc(rnd(w), rnd(h), 1.5, 0, Math.PI * 2);
    ctx.fill();
  }
};

const CaptchaInput = forwardRef((props, ref) => {
  const canvasRef = useRef(null);
  const [code, setCode] = useState(() => generateCode());
  const [value, setValue] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const refresh = useCallback(() => {
    setCode(generateCode());
    setValue("");
    setErrorMsg("");
  }, []);

  useEffect(() => {
    drawCaptcha(canvasRef.current, code);
  }, [code]);

  useImperativeHandle(
    ref,
    () => ({
      validate() {
        if (!value.trim()) {
          setErrorMsg(
            l.g("certificate.captcha_required", "Vui lòng nhập mã xác nhận")
          );
          return false;
        }
        if (value.trim().toLowerCase() !== code.toLowerCase()) {
          setErrorMsg(
            l.g(
              "certificate.captcha_invalid",
              "Mã xác nhận không đúng. Vui lòng thử lại."
            )
          );
          refresh();
          return false;
        }
        setErrorMsg("");
        return true;
      },
      reset: refresh,
    }),
    [code, value, refresh]
  );

  return (
    <div className="captcha-wrapper">
      <div className={`captcha-inline${props.wideLayout ? " captcha-inline--wide" : ""}`}>
        <div className="captcha-canvas-row">
          <canvas
            ref={canvasRef}
            width={CANVAS_W}
            height={CANVAS_H}
            className="captcha-canvas"
            aria-label="CAPTCHA"
          />
          <button
            type="button"
            className="captcha-refresh-btn"
            onClick={refresh}
            title={l.g("certificate.captcha_refresh", "Làm mới mã")}
          >
            <i className="fas fa-sync-alt"></i>
          </button>
        </div>
        <div className="captcha-input-wrap">
          <input
            type="text"
            className={`captcha-input${errorMsg ? " captcha-input--error" : ""}`}
            placeholder={l.g("certificate.captcha_label", "Nhập mã xác nhận")}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              if (errorMsg) setErrorMsg("");
            }}
            maxLength={CODE_LENGTH + 1}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      </div>
      {errorMsg && (
        <div className="captcha-error-msg">
          <i className="fas fa-exclamation-circle"></i> {errorMsg}
        </div>
      )}
    </div>
  );
});

export default CaptchaInput;
